import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../../modules/auth/services/authService';
import { clearSettingsCache } from '../../modules/settings/services/settingsService';
import { tokens } from '../../services/storage/storage';
import type { Role } from '../../constants/roles';
import type { AuthState, User } from '../../modules/auth/types/authTypes';

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(true);

  // Restore session on page load.
  useEffect(() => {
    if (!tokens.get().accessToken) {
      setBooting(false);
      return;
    }
    authService
      .me()
      .then(setUser)
      .catch(() => tokens.clear())
      .finally(() => setBooting(false));
  }, []);

  // Refresh token expired somewhere in the app -> back to login.
  useEffect(() => {
    const onLogout = () => setUser(null);
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  const login = useCallback(async (phone: string, password: string) => {
    const data = await authService.login(phone, password);
    tokens.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      /* ignore: we log out locally anyway */
    }
    tokens.clear();
    clearSettingsCache();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      booting,
      login,
      logout,
      isStaff: user?.role === 'owner' || user?.role === 'office',
      can: (...roles: Role[]) => !!user && roles.includes(user.role),
    }),
    [user, booting, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
